import { useMutation, useQueryClient } from "@tanstack/react-query";

import { User } from "../../../../types/types";

import { followEntityService } from "../../../Entity/api/services/follow";
import { entityKeys } from "../../../Entity/api/queryKeys";

import { followUserService } from "../services/follow";
import { userKeys } from "../queryKeys";

interface UseFollowMutationProps {
  onSuccess?: (isFollowing: boolean) => void;
  onError?: (error: Error) => void;
}

export const useFollowMutation = ({ onSuccess, onError }: UseFollowMutationProps = {}) => {
  const queryClient = useQueryClient();

  const followUser = useMutation({
    mutationFn: followUserService,
    onSuccess: (isFollowing, userId) => {
      queryClient.invalidateQueries({ queryKey: ["NetworkList"] });
      queryClient.invalidateQueries({ queryKey: ["UserProfile"] });
      queryClient.invalidateQueries({ queryKey: ["entityFollowers"] });
      queryClient.invalidateQueries({ queryKey: ["entityFollowing"] });
      queryClient.invalidateQueries({ queryKey: userKeys.profile(String(userId)) });

      onSuccess?.(isFollowing);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });

  const followEntity = useMutation({
    mutationFn: (entityId: number) => followEntityService(entityId),
    onSuccess: (isFollowing, entityId) => {
      queryClient.invalidateQueries({ queryKey: ["NetworkList"] });
      queryClient.invalidateQueries({ queryKey: ["EntityProfile"] });
      queryClient.invalidateQueries({ queryKey: entityKeys.details(String(entityId)) });

      onSuccess?.(isFollowing);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });

  const follow = (user: User) => {
    if (user.entity?.id) {
      followEntity.mutate(user.entity.id);
    } else if (user.id) {
      followUser.mutate(user.id);
    }
  };

  return {
    follow,
    isLoading: followUser.isPending || followEntity.isPending,
    error: followUser.error || followEntity.error,
  };
};
